/**
 * The header's scope chips: the URL parameters the current screen declares it reads, and no others
 * (issue 350, owner ruling 2026-10-09, `docs/adr/9054`).
 *
 * The address bar still carries every parameter from screen to screen (`rail-layout.ts`,
 * `railNavigationParams`; G2.2, a drill keeps its context). What changes is what the header SAYS:
 * a chip is a statement that the figures on this screen are conditioned by that value, and a
 * carried parameter the screen never reads conditions nothing. On Service vs stock, a markdown
 * option key from the page before was such a chip.
 *
 * Where the declaration lives:
 * - a chapter route declares `urlParams` in its manifest (`src/features/manifest.ts`);
 * - a shell route (the Cockpit, My work, Profile, User management, the anatomy reference) is
 *   declared in {@link SHELL_URL_PARAMS} below, since the shell has no manifest.
 *
 * A route with NO declaration shows every parameter, as before issue 350 — never none: a missing
 * declaration must not hide a perimeter a screen depends on (G16). `src/app/chrome/url-param-census.test.ts`
 * fails on a route without one, and on a declaration that differs from what the screen reads.
 */

import { VOCABULARY_DEPARTMENT_FAMILY, VOCABULARY_SEASON } from '../../api/vocabulary-labels';
import type { FeatureRoute } from '../../features/manifest';
import { PARAMETER_FAMILIES, PARAMETER_FAMILY_IDS } from '../../features/parameter-families';
import { ADMIN_USERS_PATH, SEARCH_PARAM } from '../routes/admin/constants';
import { FEEDBACK_PATH, FEEDBACK_TRIAGE_PATH } from '../routes/feedback/constants';
import { MY_WORK_PATH } from '../routes/my-work/my-work';
import { PROFILE_PATH } from '../routes/profile/ProfileScreen';
import { ANATOMY_PATH, HOME_PATH } from '../routes/router';

/**
 * The shell's routes and the parameters each reads. `src/app/chrome/url-param-census.test.ts` derives the
 * same lists from the screens' code and fails when they differ.
 */
export const SHELL_URL_PARAMS: Readonly<Record<string, readonly string[]>> = {
  // The Cockpit: its run, the exit runs it summarises, the market and date; and the app/stage/section
  // keys its tiles write into the links they open (and keys: «Promozioni e campagne ▸ Parametri»,
  // a common entry of the rail the App column reads, opens the register on the campaign family).
  [HOME_PATH]: ['app', 'asOf', 'country', 'exitLaneRunId', 'exitRunId', 'keys', 'runId', 'section', 'stage'],
  [MY_WORK_PATH]: ['app', 'keys', 'runId', 'section', 'stage'],
  [PROFILE_PATH]: [],
  // My reports (bata-platform#686): the person's own list, bounded by who they are, not by a parameter.
  [FEEDBACK_PATH]: [],
  // Report triage: its filters are the screen's own state, not the address's.
  [FEEDBACK_TRIAGE_PATH]: [],
  // The search box, which the router reads for the screen.
  [ADMIN_USERS_PATH]: [SEARCH_PARAM],
  // Development builds only: the state the reference renders.
  [ANATOMY_PATH]: ['state'],
};

/**
 * Never a chip, declared or not. A run's identifier (planner review, 2026-09-25: "eliminare le righe
 * codice stringhe dei run"): a UUID-shaped string nobody reads; every run-scoped screen states its
 * run in words and the id stays in the address bar, where it keeps the view a link. Matched on the
 * KEY's shape, so the chrome still names no chapter's parameter.
 */
const NEVER_A_CHIP = /runid$/i;

/**
 * The parameters the screen at `path` declares, or `undefined` when it declares none.
 * `route` is the chapter route the router resolved for the path, if any.
 */
export function declaredUrlParams(path: string, route: Pick<FeatureRoute, 'urlParams'> | undefined): readonly string[] | undefined {
  if (route !== undefined) return route.urlParams;
  return SHELL_URL_PARAMS[path];
}

/**
 * The chips to draw: the declared parameters present in the address, in a stable order so the
 * chips do not reshuffle as the planner navigates. `declared` undefined: every parameter.
 */
export function scopeChips(
  params: Readonly<Record<string, string>>,
  declared: readonly string[] | undefined,
): readonly (readonly [string, string])[] {
  const shown = declared === undefined ? null : new Set(declared);
  return Object.entries(params)
    .filter(([key]) => !NEVER_A_CHIP.test(key))
    .filter(([key]) => shown === null || shown.has(key))
    .sort(([a], [b]) => a.localeCompare(b));
}

/**
 * The words a chip names its value with: the catalogue key of each parameter a screen declares
 * (A6, owner decision 2026-10-10). A chip reads «Negozio: 5262», not a bare value that could be a
 * store, an article or a week. Keys are literals so the catalogue check can see them. A parameter
 * with no entry draws its value alone, the key in the chip's title: a new parameter is a missing
 * label, never a missing chip.
 */
const CHIP_LABEL_KEYS: Readonly<Record<string, string>> = {
  a: 'nav.scopeChip.a',
  action: 'nav.scopeChip.action',
  actor: 'nav.scopeChip.actor',
  alert: 'nav.scopeChip.alert',
  'ap.apps': 'nav.scopeChip.ap_apps',
  'f.article': 'nav.scopeChip.f_article',
  'f.destination': 'nav.scopeChip.f_destination',
  'f.origin': 'nav.scopeChip.f_origin',
  'f.purpose': 'nav.scopeChip.f_purpose',
  // «Asignación inicial»'s lines page: the constraint that bound a line (`docs/adr/5001`).
  'f.reason': 'nav.scopeChip.f_reason',
  'f.state': 'nav.scopeChip.f_state',
  'ap.may': 'nav.scopeChip.ap_may',
  app: 'nav.scopeChip.app',
  article: 'nav.scopeChip.article',
  articles: 'nav.scopeChip.articles',
  asOf: 'nav.scopeChip.asOf',
  b: 'nav.scopeChip.b',
  // «Surtido» (`docs/adr/4001`): the store band, the cluster, the category, the flag and the decision a page is narrowed to.
  band: 'nav.scopeChip.band',
  // «Surtido»: the articles of one gender or brand a page is narrowed to (`~`: those the product master states none for).
  brand: 'nav.scopeChip.brand',
  brandKey: 'nav.scopeChip.brandKey',
  by: 'nav.scopeChip.by',
  bySize: 'nav.scopeChip.bySize',
  carry: 'nav.scopeChip.carry',
  channelKey: 'nav.scopeChip.channelKey',
  cluster: 'nav.scopeChip.cluster',
  class: 'nav.scopeChip.class',
  clusterKey: 'nav.scopeChip.clusterKey',
  compareKind: 'nav.scopeChip.compareKind',
  country: 'nav.scopeChip.country',
  decision: 'nav.scopeChip.decision',
  flag: 'nav.scopeChip.flag',
  cover: 'nav.scopeChip.cover',
  countryKey: 'nav.scopeChip.countryKey',
  department: 'nav.scopeChip.department',
  doors: 'nav.scopeChip.doors',
  exitPlanId: 'nav.scopeChip.exitPlanId',
  explain: 'nav.scopeChip.explain',
  figures: 'nav.scopeChip.figures',
  forecastRun: 'nav.scopeChip.forecastRun',
  from: 'nav.scopeChip.from',
  gender: 'nav.scopeChip.gender',
  group: 'nav.scopeChip.group',
  guide: 'nav.scopeChip.guide',
  isProposed: 'nav.scopeChip.isProposed',
  key: 'nav.scopeChip.key',
  laneCostAbsent: 'nav.scopeChip.laneCostAbsent',
  lanePriced: 'nav.scopeChip.lanePriced',
  keys: 'nav.scopeChip.keys',
  lane: 'nav.scopeChip.lane',
  lines: 'nav.scopeChip.lines',
  markdownView: 'nav.scopeChip.markdownView',
  // «Surtido»: the measure the category grid draws.
  matrixMeasure: 'nav.scopeChip.matrixMeasure',
  market: 'nav.scopeChip.market',
  // «Perimetri» (ADR 6028): the library entry the editor opens on, and the kind of entry.
  entry: 'nav.scopeChip.entry',
  kind: 'nav.scopeChip.kind',
  measure: 'nav.scopeChip.measure',
  minorUnit: 'nav.scopeChip.minorUnit',
  modelVersionId: 'nav.scopeChip.modelVersionId',
  module: 'nav.scopeChip.module',
  needsAttention: 'nav.scopeChip.needsAttention',
  node: 'nav.scopeChip.node',
  month: 'nav.scopeChip.month',
  object: 'nav.scopeChip.object',
  objectiveKind: 'nav.scopeChip.objectiveKind',
  openOption: 'nav.scopeChip.openOption',
  openingUnits: 'nav.scopeChip.openingUnits',
  option: 'nav.scopeChip.option',
  optionId: 'nav.scopeChip.optionId',
  optionKey: 'nav.scopeChip.optionKey',
  optionKeys: 'nav.scopeChip.optionKeys',
  overdue: 'nav.scopeChip.overdue',
  overridden: 'nav.scopeChip.overridden',
  owner: 'nav.scopeChip.owner',
  parameter: 'nav.scopeChip.parameter',
  // «Surtido»: a peer group is a band or a cluster written for the address (`b2`, `cNORTH|b2`): a code, so it sits behind the «id» (ADR 9057).
  peerGroup: 'nav.scopeChip.peerGroup',
  penaltyBasis: 'nav.scopeChip.penaltyBasis',
  priceStream: 'nav.scopeChip.priceStream',
  projectionArchived: 'nav.scopeChip.projectionArchived',
  proposalsPanel: 'nav.scopeChip.proposalsPanel',
  proposalsView: 'nav.scopeChip.proposalsView',
  q: 'nav.scopeChip.q',
  queue: 'nav.scopeChip.queue',
  realisationAbsent: 'nav.scopeChip.realisationAbsent',
  repairsSizeRun: 'nav.scopeChip.repairsSizeRun',
  routingDecisionId: 'nav.scopeChip.routingDecisionId',
  row: 'nav.scopeChip.row',
  rule: 'nav.scopeChip.rule',
  runIsBroken: 'nav.scopeChip.runIsBroken',
  salesBy: 'nav.scopeChip.salesBy',
  salesFrom: 'nav.scopeChip.salesFrom',
  salesTo: 'nav.scopeChip.salesTo',
  salvageFraction: 'nav.scopeChip.salvageFraction',
  scenario: 'nav.scopeChip.scenario',
  scope: 'nav.scopeChip.scope',
  season: 'nav.scopeChip.season',
  section: 'nav.scopeChip.section',
  sellThroughAbsent: 'nav.scopeChip.sellThroughAbsent',
  severity: 'nav.scopeChip.severity',
  simulation: 'nav.scopeChip.simulation',
  // The simulation launched from Campaigns' and exit consolidation's compose pages (owner ruling 2026-10-20).
  campaignSimulation: 'nav.scopeChip.campaignSimulation',
  exitSimulation: 'nav.scopeChip.exitSimulation',
  // Compacting › Simulazione (platform ADR 9056): the simulation launched from it, and the one it is relaunched from.
  moveSimulation: 'nav.scopeChip.moveSimulation',
  moveSimulationFrom: 'nav.scopeChip.moveSimulationFrom',
  sla: 'nav.scopeChip.sla',
  sort: 'nav.scopeChip.sort',
  stage: 'nav.scopeChip.stage',
  state: 'nav.scopeChip.state',
  stockOut: 'nav.scopeChip.stockOut',
  'stock.article': 'nav.scopeChip.stock_article',
  'stock.store': 'nav.scopeChip.stock_store',
  store: 'nav.scopeChip.store',
  sub: 'nav.scopeChip.sub',
  to: 'nav.scopeChip.to',
  user: 'nav.scopeChip.user',
  view: 'nav.scopeChip.view',
  week: 'nav.scopeChip.week',
  weeksRemaining: 'nav.scopeChip.weeksRemaining',
  window: 'nav.scopeChip.window',
  // The process calendar's year (ADR 9068).
  year: 'nav.scopeChip.year',
};

/**
 * The parameters whose value is an identifier the platform minted — a scenario, a forecast run, a
 * model version, a simulation: not a code a person reads and not one a vocabulary publishes words
 * for. The chip names the parameter and puts the identifier behind the small «id» (tooltip and
 * copy, ADR 9057), instead of printing it («Scenario: system.sandbox.CO», audit 2, A3/A4). The
 * identifier's own shape is NOT parsed for a name: a scenario is the sandbox by the contract's
 * `sandbox` flag, never by what its id looks like.
 */
const OPAQUE_ID_PARAMS: ReadonlySet<string> = new Set([
  'campaignSimulation', 'exitSimulation', 'forecastRun', 'modelVersionId', 'moveSimulation', 'moveSimulationFrom', 'routingDecisionId', 'scenario', 'simulation',
  // «Surtido» and «Asignación inicial» (ADR 9070 R5, 9083): the line a «Por qué» explains, the band's index, the peer group written for the address and the
  // constraint that bound a line are identifiers or codes; the page names each in words and the chip keeps the identifier behind «id».
  'row', 'band', 'peerGroup', 'f.reason',
]);

/** Whether a chip's value is an identifier to put behind the «id» rather than print. */
export function scopeChipValueIsOpaqueId(param: string): boolean {
  return OPAQUE_ID_PARAMS.has(param);
}

/** The catalogue key of a parameter's label, or `undefined` when it has none yet. */
export function scopeChipLabelKey(param: string): string | undefined {
  return Object.hasOwn(CHIP_LABEL_KEYS, param) ? CHIP_LABEL_KEYS[param] : undefined;
}

/**
 * The words a chip names its VALUE with, for the parameters whose value is one of a closed set
 * (owner decision 2026-10-11: «Raggruppa per: Reparto», not «department_family»). Each entry is
 * the catalogue key a screen already uses for that same concept — the network-health group-by,
 * the simulate stage, the write-back dimensions — so the chip and the control it mirrors cannot
 * disagree. Keys are literals so the catalogue check can see them.
 *
 * Only values read from the screens' own code are listed. A value not here (a new enum member, a
 * date, a market code) draws as it is: an unknown word is a missing label, never a missing chip.
 * Identifiers — store, article and option keys, markets, dates, run ids — are never mapped.
 */
const CHIP_VALUE_KEYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // «Surtido»: the words of the closed sets its pages put in the address (the same keys the controls use).
  action: {
    add_now: 'preseason.assortment.action.add_now',
    add_next_buy: 'preseason.assortment.action.add_next_buy',
    remove: 'preseason.assortment.action.remove',
    keep: 'preseason.assortment.action.keep',
  },
  decision: {
    accept: 'preseason.assortment.decision.accept',
    reject: 'preseason.assortment.decision.reject',
    undecided: 'preseason.assortment.decision.undecided',
  },
  flag: {
    under_assorted: 'preseason.assortment.flag.under_assorted',
    over_assorted: 'preseason.assortment.flag.over_assorted',
    missing: 'preseason.assortment.flag.missing',
    bounded: 'preseason.assortment.flag.bounded',
    refused: 'preseason.assortment.flag.refused',
  },
  matrixMeasure: {
    models: 'preseason.assortment.measure.models',
    salesPerModel: 'preseason.assortment.measure.salesPerModel',
    sales: 'preseason.assortment.measure.sales',
    units: 'preseason.assortment.measure.units',
    margin: 'preseason.assortment.measure.margin',
    carrying: 'preseason.assortment.measure.carrying',
    under: 'preseason.assortment.measure.under',
    over: 'preseason.assortment.measure.over',
    missing: 'preseason.assortment.measure.missing',
    tail: 'preseason.assortment.measure.tail',
    addNow: 'preseason.assortment.measure.addNow',
    remove: 'preseason.assortment.measure.remove',
  },
  // «Surtido»: `~` is the platform's value for the articles the product master states no gender (or brand) for.
  brand: { '~': 'preseason.assortment.attr.noBrand' },
  gender: { '~': 'preseason.assortment.attr.noGender' },
  app: {
    pricing: 'nav.app.pricing',
    promotions: 'nav.app.promotions',
    replenishment: 'nav.app.replenishment',
    compacting: 'nav.app.compacting',
    management: 'nav.app.management',
    initialAllocation: 'nav.app.initialAllocation',
    assortment: 'nav.app.assortment',
  },
  doors: {
    drawn: 'inseason.doors.filter.drawnValue',
    noSales: 'inseason.doors.filter.noSalesValue',
  },
  figures: {
    measured: 'overview.benefits.measured',
  },
  articles: {
    served: 'inseason.store.articlesFilter.servedValue',
  },
  cover: {
    none: 'inseason.store.coverFilter.noneValue',
  },
  lines: {
    served: 'inseason.store.linesFilter.servedValue',
  },
  overdue: {
    true: 'inseason.workbench.overdueFilter.value',
  },
  // The override lines' narrowing (ADR 9069): yes / no, in the words the table's own filter uses.
  overridden: { true: 'strategic.overrides.yes', false: 'strategic.overrides.no' },
  // The consolidation pages' narrowing (ADR 9069): each is a yes.
  isProposed: { true: 'strategic.overrides.yes' },
  laneCostAbsent: { true: 'strategic.overrides.yes' },
  realisationAbsent: { true: 'strategic.overrides.yes' },
  repairsSizeRun: { true: 'strategic.overrides.yes' },
  sellThroughAbsent: { true: 'strategic.overrides.yes' },
  projectionArchived: { true: 'strategic.overrides.yes', false: 'strategic.overrides.no' },
  // Health ▸ Lookup (`docs/adr/3004`): each pair with its sizes.
  bySize: { true: 'strategic.overrides.yes' },
  by: {
    department_family: 'inseason.networkHealth.by.department_family',
    store: 'inseason.networkHealth.by.store',
    article: 'inseason.networkHealth.by.article',
    department: 'foundations.alertGroups.by.department',
    country: 'foundations.alertGroups.by.country',
    subject_kind: 'inseason.writeBackGroups.by.subject_kind',
    run: 'inseason.writeBackGroups.by.run',
  },
  compareKind: {
    terminal_constraint: 'inseason.objective.terminalConstraint',
    leftover_penalty: 'inseason.objective.leftoverPenalty',
  },
  // The register's key family (ADR 9064): the family's subject, never its prefix.
  keys: Object.fromEntries(PARAMETER_FAMILY_IDS.map((id) => [PARAMETER_FAMILIES[id].prefix, PARAMETER_FAMILIES[id].nameKey])),
  measure: {
    cycle: 'inseason.exchange.measure.cycleService',
  },
  module: {
    inseason: 'inseason.cycles.module.inseason',
    exit: 'inseason.cycles.module.exit',
  },
  needsAttention: {
    true: 'inseason.writeBackLog.attentionFilter.value',
  },
  objectiveKind: {
    terminal_constraint: 'inseason.objective.terminalConstraint',
    leftover_penalty: 'inseason.objective.leftoverPenalty',
  },
  penaltyBasis: {
    all_leftover: 'inseason.penaltyBasis.allLeftover',
    above_target: 'inseason.penaltyBasis.aboveTarget',
  },
  priceStream: {
    clearance: 'inseason.stream.clearance',
  },
  proposalsView: {
    store: 'inseason.proposalsView.option.store',
    article: 'inseason.proposalsView.option.article',
    line: 'inseason.proposalsView.option.line',
  },
  queue: {
    mine: 'foundations.alerts.queue.mine',
    claimable: 'foundations.alerts.queue.claimable',
    unassigned: 'foundations.alerts.queue.unassigned',
    all: 'foundations.alerts.queue.all',
  },
  section: {
    pricing: 'nav.app.pricing',
    promotions: 'nav.app.promotions',
    replenishment: 'nav.app.replenishment',
    compacting: 'nav.app.compacting',
    management: 'nav.app.management',
  },
  // The alert's own severity code, in the words the queue gives its class (the Workbench's «Critical» figure, ADR 9069).
  severity: {
    critical: 'threshold.critical',
    high: 'threshold.critical',
  },
  // «Attività utenti»'s ranking (ADR 9059) — the words its «Ordina per» shows.
  sort: {
    last_seen: 'foundations.userActivity.sort.lastSeen',
    logins: 'foundations.userActivity.sort.logins',
    failed_logins: 'foundations.userActivity.sort.failedLogins',
    session_time: 'foundations.userActivity.sort.sessionTime',
    actions: 'foundations.userActivity.sort.actions',
  },
  stockOut: {
    counted: 'inseason.allocationStock.stockOuts.value',
  },
  stage: {
    distribute: 'inseason.simulate.stage.distribute',
    price: 'inseason.simulate.stage.price',
  },
  state: {
    open: 'foundations.alerts.state.open',
    snoozed: 'foundations.alerts.state.snoozed',
    decided: 'foundations.alerts.state.decided',
    set: 'foundations.parameters.filter.state.set',
    unset: 'foundations.parameters.filter.state.unset',
    pending: 'foundations.parameters.filter.state.pending',
    // «Asignación inicial»'s runs page: the verdict the list is narrowed to.
    allocated: 'launch.initial.outcome.allocated',
    not_allocable: 'launch.initial.outcome.not_allocable',
    not_selected: 'launch.initial.outcome.not_selected',
  },
  view: {
    today: 'inseason.networkHealth.l0.view.today',
    weeks: 'inseason.networkHealth.l0.view.weeks',
    groups: 'foundations.alertGroups.view.groups',
    alerts: 'foundations.alertGroups.view.alerts',
    lines: 'inseason.writeBackGroups.view.lines',
    // «Asignación inicial»: only the articles it cannot allocate, and the cells of a run served or left out (`all`, `grid` and `table` are words already).
    notAllocable: 'launch.initial.chip.viewNotAllocable',
    served: 'launch.initial.cellOutcome.served',
    leftOut: 'launch.initial.cellOutcome.excluded',
  },
};

/** The catalogue key of a chip VALUE's words, or `undefined` when the parameter or the value has none. */
export function scopeChipValueKey(param: string, value: string): string | undefined {
  if (!Object.hasOwn(CHIP_VALUE_KEYS, param)) return undefined;
  const values = CHIP_VALUE_KEYS[param]!;
  return Object.hasOwn(values, value) ? values[value] : undefined;
}

/**
 * The parameters whose value is a code the platform publishes words for
 * (`GET /vocabularies/{vocabulary}`, `src/api/vocabulary-labels.ts`): `department` carries a
 * department family (`ATHLETIC`), `season` a season code. The chrome asks for those words and
 * falls back to the code while they load or where none is published.
 */
const CHIP_VOCABULARIES: Readonly<Record<string, string>> = {
  department: VOCABULARY_DEPARTMENT_FAMILY,
  season: VOCABULARY_SEASON,
};

/** The vocabulary a chip's value is read in, or `undefined` for a parameter whose value is not a vocabulary code. */
export function scopeChipVocabulary(param: string): string | undefined {
  return Object.hasOwn(CHIP_VOCABULARIES, param) ? CHIP_VOCABULARIES[param] : undefined;
}
